import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import styles from "./Login.module.scss";
import { toast } from "react-toastify";
//import admin login api
import loginWithEmail from "../../services/auth/loginWithEmail.js"
import signupUser from "../../services/auth/signupUser.js"
import AUTH_TOASTS from "../../utils/constants/toastMesages/authToasts.js";
import { setAuthSession } from "../../renderer/platform/authStorage.js";
import { openExternalUrl } from "../../renderer/platform/index.js";
import { APP_CONFIG } from "../../utils/runtimeConfig.js";


/**
 * Login component renders a login form for user authentication.
 *
 * @component
 * @example
 * return (
 *   <Login />
 * )
 */
function Login() {
  //Use navigate hook for navigating to other routes
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  //Tracking if create new password is clicked or not 
  const [mode, setMode] = useState("login");

  //User credentials object for sending/fetching to backend
  const [credentials, setCredentials] = useState({
    email: "",
    name: "",
    username: "",
    password: "",
  });

  //States for tracking rember me fuctionality
  const [rememberMe, setRememberMe] = useState(false);
  //Login post request
  const handleLogin = async () => {
    try {
      const data = await loginWithEmail(credentials);
      await setAuthSession(data.token, data.user.role, rememberMe);
      toast.success(AUTH_TOASTS.SIGNIN_SUCCESFUL);
      navigate("/billing");
      window.location.reload();
    } catch (error) {
      toast.error(AUTH_TOASTS.SIGNIN_UNSUCCESFUL);
    }
  };

  //Handler function for signingup the new user 
  const handleSignUp = async () => {
    try {
      await signupUser(credentials);
      toast.success(AUTH_TOASTS.REGISTER_SUCCESFUL);
    }
    catch (error) {
      toast.error(AUTH_TOASTS.REGISTER_UNSUCCESFUL);
    }
  }

  const { search, hash } = useLocation();
  const oauthRequestRef = useRef({ search, hash, rememberMe });

  useEffect(() => {
    const { search: initialSearch, hash: initialHash, rememberMe: initialRememberMe } = oauthRequestRef.current;
    // Combine ?query and #hash so it works in any router mode
    const query = initialSearch || initialHash.replace(/^#/, '?');
    const code = new URLSearchParams(query).get('code');

    if (!code) return;

    (async () => {
      try {
        const res = await fetch(`${APP_CONFIG.apiBaseUrl}/auth/facebook`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code })
        });

        if (!res.ok) throw new Error("Backend authentication failed");

        const data = await res.json();

        await setAuthSession(data.accessToken, data.user.role, initialRememberMe);

        toast.success("Login successful");
        window.location.reload();
      } catch (err) {
        console.error(err);
        toast.error("Facebook login failed");
      }
    })();
  }, []);
  return (
    <div className={styles.loginContainer}>
      {/* Left Section - App Download */}
      <div className={styles.appDownloadSection}></div>

      {/* Right Section - Login Form */}
      <div className={styles.loginFormContainer}>
        <div className={styles.formInner}>
          <h1 className={styles.appTitle}>Orderly</h1>

          {/* Login Title */}
          <h2 className={styles.loginTitle}>
            Login
          </h2>
          <div>
            <div></div>
            <div>
              {mode === "login" ? (
                step === 1 ? (
                  <>
                    {/* Login Step 1 - Email */}
                    <p className={styles.inputLabel}>Phone or Email*</p>
                    <input
                      value={credentials.email}
                      onChange={(e) =>
                        setCredentials({ ...credentials, email: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setStep(2);
                      }}
                      type="text"
                      className={styles.loginInput}
                      placeholder="Enter your phone number or email address"
                    />
                    {
                      !credentials.email && <p className={styles.pleaseEnterDetailsDesign}>Please enter your email*</p>
                    }

                    <div className={styles.rememberMe}>
                      <input type="checkbox" id="remember" checked={rememberMe}
                        onChange={() => setRememberMe(!rememberMe)} />
                      <label htmlFor="remember">Remember me on this device</label>
                    </div>

                    <button className={styles.loginButton} onClick={() => { credentials.email && setStep(2) }}>
                      Login
                    </button>

                    {/* <button className={styles.createAccountButton} onClick={() => setMode("register")}>
                Create New Account
              </button> */}
                  </>
                ) : (
                  <>
                    {/* Login Step 2 - Password */}
                    <div>
                      <ArrowLeft 
                        className={styles.backArrow}
                        onClick={() => setStep(1)}
                        title="Back to email"
                      />
                    </div>
                    <p className={styles.inputLabel}>Password</p>
                    <div className="d-flex align-items-center gap-3">
                      <input
                        value={credentials.password}
                        onChange={(e) =>
                          setCredentials({ ...credentials, password: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleLogin();
                        }}
                        type="password"
                        className={styles.loginInput}
                        placeholder="Enter your password"
                        autoFocus
                      />
                    </div>
                    {
                      !credentials.password && <p className={styles.pleaseEnterDetailsDesign}>Please enter your password*</p>
                    }

                    <button className={`${styles.loginButton} ${styles.fullWidth}`} onClick={handleLogin}>
                      Login
                    </button>
                  </>
                )
              ) : (
                <>
                  <h3 className="mb-3">Create New Account</h3>
                  <p className={styles.inputLabel}>Email*</p>
                  <input
                    type="email"
                    className={styles.loginInput}
                    placeholder="Enter your email"
                    value={credentials.email}
                    onChange={(e) =>
                      setCredentials({ ...credentials, email: e.target.value })
                    }
                  />

                  <p className={styles.inputLabel}>Name*</p>
                  <input
                    type="text"
                    className={styles.loginInput}
                    placeholder="Enter your name"
                    value={credentials.name}
                    onChange={(e) =>
                      setCredentials({ ...credentials, name: e.target.value })
                    }
                  />

                  <p className={styles.inputLabel}>User Name*</p>
                  <input
                    type="text"
                    className={styles.loginInput}
                    placeholder="Enter a your username"
                    value={credentials.username}
                    onChange={(e) =>
                      setCredentials({ ...credentials, username: e.target.value })
                    }
                  />

                  <p className={styles.inputLabel}>Password*</p>
                  <input
                    type="password"
                    className={styles.loginInput}
                    placeholder="Enter a strong password"
                    value={credentials.password}
                    onChange={(e) =>
                      setCredentials({ ...credentials, password: e.target.value })
                    }
                  />

                  <button onClick={() => handleSignUp()} className={styles.loginButton}>
                    Register
                  </button>

                  <button className={styles.createAccountButton} onClick={() => {
                    setMode("login");
                    setStep(1);
                  }}>
                    Back to Login
                  </button>
                </>
              )}
              {/* <GoogleLoginButton rememberMe={rememberMe}/>
        <FacebookLoginButton/> */}
              <div className={styles.divider}></div>
              <div style={{ borderRadius: "10px" }}>
              </div>
            </div>
          </div>
          <button className={styles.backLink} onClick={() => void openExternalUrl(APP_CONFIG.frontendUrl)}>
            Back to {APP_CONFIG.appName}
          </button>
        </div>
        <footer className={styles.footer}>
          Copyright © {new Date().getFullYear()}  - All rights
          reserved.
        </footer>
      </div>
    </div>
  );
}

export default Login;
